import * as React from "react";
import classNames from "classnames";
import { connect } from "react-redux";
import { StoreState } from "../../reducers/root-reducer";
import { WithStyles, Theme, createStyles, withStyles } from "@material-ui/core";
import { InjectedTranslateProps, translate } from "react-i18next";
import Typography from "@material-ui/core/Typography";
import { ConnectedSheet } from "../sheet";
import { Table } from "../../api/tables";
import { ConnectedPlannerListTable } from "./table";
import { ConnectedPlannerListMovingReservation } from "./moving-reservation";
import { ConnectedTableLinkInfobar } from "../overview/table-link-infobar";
import { OccupiedDay } from "../../api/occupied-days";
import {
	getSelectedOccupiedDay,
	isAreaLocked
} from "../../util/general-utility";
import { ConnectedUnsortedReservationSection } from "./unsorted-reservation-section";

interface State {
	sortedTables: Table[];
	displayLock: boolean;
}

export interface PlannerListProps {
	readOnly?: Boolean;
}

interface ConnectedPlannerListProps extends WithStyles<typeof styles> {
	tables: Table[];
	activeAreaId: string;
	selectedDate: Date;
	occupiedDays: OccupiedDay[];
	showAlleAreasToggle: boolean;
}

type Props = PlannerListProps &
	ConnectedPlannerListProps &
	InjectedTranslateProps;

export class PlannerList extends React.Component<Props, State> {
	public state: State = {
		sortedTables: [],
		displayLock: false
	};

	public componentDidMount() {
		this.sortTables();
		this.getLockStatus();
	}

	public componentWillReceiveProps(nextProps: Props) {
		if (
			nextProps.tables !== this.props.tables ||
			nextProps.occupiedDays !== this.props.occupiedDays ||
			nextProps.showAlleAreasToggle !== this.props.showAlleAreasToggle ||
			nextProps.activeAreaId !== this.props.activeAreaId
		) {
			this.sortTables(nextProps);
			this.getLockStatus(nextProps);
		}
	}

	public shouldComponentUpdate(nextProps: Props, nextState: State) {
		return (
			nextProps.occupiedDays !== this.props.occupiedDays ||
			nextProps.showAlleAreasToggle !== this.props.showAlleAreasToggle ||
			nextProps.activeAreaId !== this.props.activeAreaId ||
			nextProps.readOnly !== this.props.readOnly ||
			nextProps.tables !== this.props.tables ||
			nextState !== this.state
		);
	}

	private sortTables = (props = this.props) => {
		const { tables, activeAreaId, showAlleAreasToggle } = props;

		var sortedTables = tables
			.sort((a, b) => a.sortIndex - b.sortIndex)

		if(showAlleAreasToggle) {
			// zweimal ändern!
			const indoorAreas = [
				"64eeed7a1b169e675e05e30f",
				"64eeedaa1b169e675e05e315",
				"64eeedb61b169e675e05e317",
				"64eeedc51b169e675e05e319",
				"64eeeddb1b169e675e05e31d"
			]

			sortedTables = sortedTables
				.filter(t => (indoorAreas.indexOf(t.areaId) !== -1));
		} else {
			sortedTables = sortedTables
				.filter(t => t.areaId === activeAreaId);
		}

		this.setState({ sortedTables });
	};

	private getLockStatus = (props = this.props) => {
		const { activeAreaId, selectedDate, occupiedDays } = props;

		if (occupiedDays.length === 0) return;

		const occupiedDay = getSelectedOccupiedDay(occupiedDays, selectedDate);
		const isLockdown = (occupiedDay && occupiedDay.lockdown)
		const lockedAreas = (occupiedDay && occupiedDay.lockedAreas) || [];

		this.setState({
			displayLock: isLockdown || lockedAreas.indexOf(activeAreaId) > -1
		});
	};

	public render() {
		const { classes, readOnly } = this.props;
		const { sortedTables, displayLock } = this.state;

		if (sortedTables.length === 0) return null;

		return (
			<div>
				<ConnectedSheet
					maxWidth={1200}
					paddingLeft={0}
					paddingTop={0}
					paddingRight={0}
					paddingBottom={0}
				>
					{sortedTables.map(table => (
						<ConnectedPlannerListTable
							key={table.id}
							readOnly={readOnly}
							{...table}
						/>
					))}
					<ConnectedUnsortedReservationSection readOnly={readOnly} />
				</ConnectedSheet>

				{readOnly !== true && (
					<React.Fragment>
						<ConnectedPlannerListMovingReservation />
						<ConnectedTableLinkInfobar
							rootClass={classes.tableLinkInfobar}
						/>
					</React.Fragment>
				)}
				<div
					className={classNames(classes.lockdownOverlay, {
						[classes.lockdownOverlayVisible]: displayLock
					})}
				>
					<Typography className={classes.lockdownLabel}>
						Gesperrt
					</Typography>
				</div>
			</div>
		);
	}
}

const mapStateToProps = (state: StoreState) => ({
	tables: state.tables,
	activeAreaId: state.overviewActiveAreaId,
	selectedDate: state.date,
	occupiedDays: state.occupiedDays,
	showAlleAreasToggle: state.overviewAllAreasToggle,
});

const styles = (theme: Theme) =>
	createStyles({
		tableLinkInfobar: {
			height: 100,
			transform: "translate3d(0, 110%, 0)",
			top: "auto",
			bottom: 0
		},
		lockdownOverlay: {
			position: "absolute",
			top: 0,
			left: 0,
			width: "100%",
			height: "100%",
			background: "rgba(255, 255, 255, 0.5)",
			opacity: 0,
			zIndex: 49,
			pointerEvents: "none",
			userSelect: "none",
			transition: "opacity 0.3s ease;"
		},
		lockdownOverlayVisible: {
			opacity: 1,
			pointerEvents: "all"
		},
		lockdownLabel: {
			position: "absolute",
			top: "50%",
			left: "50%",
			transform: "translate3d(-50%, -50%, 0) rotate(-30deg)",
			fontSize: "5rem",
			fontWeight: 500,
			color: "white",
			"-webkit-text-stroke": "3px black",
			paintOrder: "stroke fill"
		}
	});

export const ConnectedPlannerList = translate("translations")(
	withStyles(styles)(connect(mapStateToProps)(PlannerList))
);
